import React, {useMemo} from 'react';
import {Alert, Space} from 'antd';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {
  StyledActivateAccount,
  StyledActivateAccountCopyFieldsContainer
} from './StyledActivateAccount';
import {StyledKYCModalContentStepTitle} from '../../../../KYCModal/StyledKYCModal';
import {StyledKYCModalSpin} from '../../../KYCModalScreen/StyledKYCModalScreen';
import CopyField from '../../../../../CopyField';
import KYCModalScreen from '../../../KYCModalScreen';
import {alertActions} from '../../../../../../state/actions/alert.actions';
import {useCompanyName, useWireDetails} from '../../../../../../hooks';
import {kycHelpers, transactionsHelpers} from '../../../../../../utils/helpers';

const ActivateAccount = ({
  loading = false,
  error,
  warning,
  onFinish,
  ...rest
}) => {
  const companyName = useCompanyName();
  const wireDetails = useWireDetails();
  const [t] = useTranslation(['main', 'quickStart']);

  const copyFieldProps = {
    buttonType: 'icon',
    onCopy: () => alertActions.success(`${t('copied')}!`)
  }

  const {bic, iban}  = useMemo(() => transactionsHelpers.getWireDetails(wireDetails), [wireDetails]);

  // Six banking states reach this step, and only `ubo_creating` — where a company waits for its
  // beneficial owners to accept their invitations — has no wallet behind it. The stage cannot tell
  // them apart: `ubo_creating` and `ubo_completed` are both `ultimate_beneficiary_stage` and only
  // the second has an account. So decide on whether the wire details actually arrived, which also
  // covers a wallet that failed to load or create at account_stage. Before this, all of them were
  // told "Your Account has been created" above two empty fields (H1MVP-4736).
  //
  // A fetch still in flight counts as details on the way, so account_stage keeps today's spinner
  // rather than flashing the not-yet copy before the wallet lands.
  const hasAccount = Boolean(iban || bic) || loading;

  const {
    description,
    info,
    title
  } = useMemo(
    () => kycHelpers.getKYCFormDetails({t, formKey: hasAccount ? 'activateAccount' : 'activateAccountPending'}),
    [t, hasAccount]
  );

  const onOk = (e) => onFinish && onFinish(e);

  return (
    <KYCModalScreen
      enabledCancel={false}
      onOk={onOk}
      okText={t('done')}
    >
      <StyledActivateAccount
        direction='vertical'
        size='large'
        {...rest}
      >
        {warning && (
          <Alert
            className='warning-alert'
            message={warning}
            type='warning'
          />
        )}
        <Space
          direction='vertical'
          size='middle'
        >
          <StyledKYCModalContentStepTitle>
            {title}
          </StyledKYCModalContentStepTitle>
          <p>
            {description}
            <br/>
            {info(hasAccount ? 'hereAreDetails' : 'detailsWhenReady')}
          </p>
        </Space>
        {hasAccount && (
          <StyledKYCModalSpin
            size='small'
            spinning={loading}
          >
            <Space
              direction='vertical'
              size='middle'
            >
              <StyledActivateAccountCopyFieldsContainer>
                <CopyField
                  {...copyFieldProps}
                  label={`${t('accountName')}:`}
                  value={companyName}
                  data-testid='account-name'
                />
                <CopyField
                  {...copyFieldProps}
                  label='IBAN:'
                  value={iban}
                  data-testid='iban'
                />
                <CopyField
                  {...copyFieldProps}
                  label='SWIFT / BIC:'
                  value={bic}
                  data-testid='bic'
                />
              </StyledActivateAccountCopyFieldsContainer>
              <Alert
                message={error || info('alertMessage', {name: companyName})}
                type={error ? 'error' : 'warning'}
              />
            </Space>
          </StyledKYCModalSpin>
        )}
        {(!hasAccount && error) && (
          <Alert
            message={error}
            type='error'
          />
        )}
      </StyledActivateAccount>
    </KYCModalScreen>
  );
}

ActivateAccount.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.any,
  warning: PropTypes.any
}

export default ActivateAccount;
